// ─────────────────────────────────────────────────────────────────────────────
// Student-made levels: 2D obstacle courses ("obbies") built in the level
// editor (components/LevelEditor.tsx) and played in the arcade
// (components/LevelPlayer.tsx), played on dry land with real gravity, each
// player their fish in a wheeled fishbowl (drawGlobe below). A level is a grid of one-letter tiles, stored
// as one string, row by row. ocean_game_check (the migration
// 20261029_level_games.sql) checks the same rules as checkLevel below.
// ─────────────────────────────────────────────────────────────────────────────

import { drawAvatar, type Look } from './avatar';

export type Theme = 'meadow' | 'desert' | 'sunset' | 'night';
export type Level = {
  v: 1;
  w: number;
  h: number;
  /** w*h tile letters, row by row from the top. */
  tiles: string;
  theme: Theme;
  /** Floaty is lower gravity: longer, slower jumps. */
  gravity: 'normal' | 'floaty';
  /** Seconds to finish (0 = no limit). */
  time: number;
  /** The flag only counts once every coin is collected. */
  allCoins: boolean;
};

export type TileInfo = { ch: string; name: string; group: 'Blocks' | 'Moving' | 'Danger' | 'Liquids' | 'Power-ups' | 'Things' | 'Creatures' | 'Special'; tip: string };

export const TILES: TileInfo[] = [
  { ch: 'g', name: 'Grass', group: 'Blocks', tip: 'Solid ground' },
  { ch: 's', name: 'Stone', group: 'Blocks', tip: 'Solid' },
  { ch: 'b', name: 'Brick', group: 'Blocks', tip: 'Solid' },
  { ch: 'c', name: 'Crate', group: 'Blocks', tip: 'Solid' },
  { ch: 'i', name: 'Ice', group: 'Blocks', tip: 'Solid and slippery' },
  { ch: 'p', name: 'Ledge', group: 'Blocks', tip: 'Jump up through it, stand on top' },
  { ch: 'm', name: 'Slider', group: 'Moving', tip: 'A moving ledge that slides side to side until it bumps into a block (paint a few in a row for a wider one)' },
  { ch: 'v', name: 'Lift', group: 'Moving', tip: 'A moving ledge that goes up and down until it bumps into a block (paint a few in a row for a wider one)' },
  { ch: 'x', name: 'Spikes', group: 'Danger', tip: 'Touch them and you go back to the last checkpoint' },
  { ch: 'u', name: 'Cactus', group: 'Danger', tip: 'Prickly! Same as spikes' },
  { ch: 'l', name: 'Lava', group: 'Liquids', tip: 'Hot! Pours down like a waterfall, spreads 3 tiles along the ground, or fills a pit as a pool' },
  { ch: 'a', name: 'Water', group: 'Liquids', tip: 'Safe to swim in (hold jump to swim up). Flows 7 tiles. Where it meets lava, it turns to stone' },
  { ch: 'y', name: 'Shield', group: 'Power-ups', tip: 'A bubble that blocks one hit (not falling out of the level)' },
  { ch: 't', name: 'Star', group: 'Power-ups', tip: '8 seconds of invincibility: touching creatures beats them' },
  { ch: 'e', name: 'Wings', group: 'Power-ups', tip: '15 seconds of double jump: jump again in the air' },
  { ch: 'z', name: 'Rocket', group: 'Power-ups', tip: '10 seconds of extra speed' },
  { ch: 'j', name: 'Spring', group: 'Things', tip: 'Bounces you up high' },
  { ch: 'o', name: 'Coin', group: 'Things', tip: 'Collect them' },
  { ch: 'k', name: 'Key', group: 'Things', tip: 'Opens one door' },
  { ch: 'd', name: 'Door', group: 'Things', tip: 'Solid until you bring a key' },
  { ch: 'w', name: 'Flowers', group: 'Things', tip: 'Just for looks' },
  { ch: 'r', name: 'Crab', group: 'Creatures', tip: 'Walks back and forth. Jump on it!' },
  { ch: 'f', name: 'Bee', group: 'Creatures', tip: 'Flies up and down. Don’t touch!' },
  { ch: 'n', name: 'Cannon', group: 'Creatures', tip: 'A solid block that aims at players nearby and fires cannonballs. You can stand on it' },
  { ch: 'q', name: 'Robot', group: 'Creatures', tip: 'Walks back and forth and fires lasers ahead of it. Jump on it!' },
  { ch: 'B', name: 'King Crab', group: 'Special', tip: 'A boss with 5 hearts: it leaps, sends shockwaves and throws rocks. Jump on its head! The flag only works once it’s beaten' },
  { ch: 'h', name: 'Checkpoint', group: 'Special', tip: 'You come back here after a fall' },
  { ch: 'S', name: 'Start', group: 'Special', tip: 'Where players begin (only one)' },
  { ch: 'G', name: 'Flag', group: 'Special', tip: 'Reach it to win' },
];
export const tileInfo = (ch: string) => TILES.find(t => t.ch === ch);

export const SOLID = new Set(['g', 's', 'b', 'c', 'i', 'n']);   // 'n': cannons are blocks too
export const HAZARD = new Set(['x', 'u']);

// ── Liquids: lava and water, like Minecraft's ───────────────────────────────
// Each lava ('l') or water ('a') tile is a source. A liquid falls straight down
// through anything that isn't solid; where it lands (on a block, a ledge or a
// pool) it spreads sideways, getting shallower each tile (lava 3 tiles each
// way, water 7), and pours over any edge it reaches. Where water and lava meet,
// that tile turns to stone ("rock"), solid like any block. Worked out once
// from the grid (the level never changes shape while you play, except doors,
// which liquids treat as walls).

export const LAVA = 1, WATER = 2;
export type Liquid = {
  /** How full each tile is, 0 (none) … 1 (full). */
  depth: Float32Array;
  /** Falling liquid (a waterfall): drawn as a full-width stream. */
  falling: Uint8Array;
  /** Which liquid: LAVA, WATER, or 0. */
  kind: Uint8Array;
  /** Stone made where water met lava. */
  rock: Uint8Array;
};

export function computeLiquid(l: { w: number; h: number; tiles: string }): Liquid {
  const { w, h, tiles } = l;
  const n = w * h;
  const amt = new Uint8Array(n), falling = new Uint8Array(n), kind = new Uint8Array(n), rock = new Uint8Array(n);
  const wall = (x: number, y: number) => {
    if (x < 0 || x >= w) return true;
    if (y < 0 || y >= h) return false;
    const i = y * w + x, ch = tiles[i];
    return rock[i] === 1 || SOLID.has(ch) || ch === 'd' || ch === 'p';
  };
  const queue: number[] = [];
  for (let i = 0; i < n; i++) {
    if (tiles[i] === 'l') { amt[i] = 8; kind[i] = LAVA; queue.push(i); }
    else if (tiles[i] === 'a') { amt[i] = 8; kind[i] = WATER; queue.push(i); }
  }
  /** Liquid of kind k, amount a, flows into tile j (falling or not). Meeting the other liquid makes stone. */
  const flowInto = (j: number, k: number, a: number, fall: boolean) => {
    if (kind[j] && kind[j] !== k) {
      if (tiles[j] !== 'l' && tiles[j] !== 'a') { rock[j] = 1; amt[j] = 0; kind[j] = 0; falling[j] = 0; }
      return;
    }
    if (fall ? (falling[j] || amt[j] === 8) : amt[j] >= a) return;
    amt[j] = a; kind[j] = k; falling[j] = fall ? 1 : 0; queue.push(j);
  };
  // Fullest first, so a deep flow isn't blocked by a shallow one that got there earlier.
  while (queue.length) {
    let best = 0;
    for (let q = 1; q < queue.length; q++) if (amt[queue[q]] > amt[queue[best]]) best = q;
    const i = queue.splice(best, 1)[0];
    if (rock[i] || !kind[i]) continue;
    const x = i % w, y = (i - x) / w, a = amt[i], k = kind[i];
    if (y + 1 >= h) continue;                                 // pours off the bottom of the level
    const below = i + w;
    if (!wall(x, y + 1)) {
      if (amt[below] === 0 || kind[below] !== k) {
        flowInto(below, k, 8, true);
        // Pouring onto the other liquid's source: this tile becomes the stone instead.
        if (kind[below] && kind[below] !== k && tiles[i] !== 'l' && tiles[i] !== 'a') { rock[i] = 1; continue; }
        if (!rock[below]) continue;
      }
      else if (falling[below]) continue;                     // already pouring down there
      // Otherwise it rests on a pool (or on new stone), and spreads like on ground.
    }
    const step = k === LAVA ? 2 : 1;
    for (const dx of [-1, 1]) {
      if (wall(x + dx, y) || a <= step) continue;
      flowInto(i + dx, k, a - step, false);
    }
  }
  const depth = new Float32Array(n);
  for (let i = 0; i < n; i++) depth[i] = rock[i] ? 0 : falling[i] ? 1 : amt[i] / 8;
  return { depth, falling, kind, rock };
}

/**
 * The liquids in view (tiles fx…lx, fy…ly): `which` picks lava (drawn behind
 * the player) or water (drawn over it, see-through), plus the stone where they met.
 */
export function drawLiquid(g: CanvasRenderingContext2D, l: { w: number; h: number }, liq: Liquid, s: number, t: number, fx: number, lx: number, fy: number, ly: number, which: 'lava' | 'water' | 'both' = 'both') {
  for (let y = fy; y <= ly; y++) for (let x = fx; x <= lx; x++) {
    const i = y * l.w + x;
    if (liq.rock[i]) { if (which !== 'water') drawTile(g, 's', x * s, y * s, s); continue; }
    const d = liq.depth[i];
    if (!d) continue;
    const k = liq.kind[i];
    if ((k === LAVA && which === 'water') || (k === WATER && which === 'lava')) continue;
    const surface = y === 0 || liq.depth[i - l.w] === 0 || liq.kind[i - l.w] !== k;
    if (k === LAVA) drawLava(g, x * s, y * s, s, d, !!liq.falling[i], surface, t);
    else drawWater(g, x * s, y * s, s, d, !!liq.falling[i], surface, t);
  }
}

/** Water in one tile: see-through blue, a rippling top, or a falling stream. */
export function drawWater(g: CanvasRenderingContext2D, x: number, y: number, s: number, d: number, fall: boolean, surface: boolean, t: number) {
  g.save();
  const top = y + s * (1 - d);
  g.fillStyle = 'rgba(52,152,235,0.55)';
  if (fall) {
    g.fillRect(x + s * 0.1, y, s * 0.8, s);
    g.strokeStyle = 'rgba(255,255,255,0.7)'; g.lineWidth = s * 0.05;
    for (let k = 0; k < 3; k++) {
      const sx = x + s * (0.25 + k * 0.25), off = ((t * 2.8 + k * 0.41) % 1) * s;
      g.beginPath(); g.moveTo(sx, y + off - s * 0.35); g.lineTo(sx, y + off); g.stroke();
    }
  } else {
    g.beginPath(); g.moveTo(x, y + s);
    if (surface) for (let k = 0; k <= 4; k++) g.lineTo(x + (k / 4) * s, top + Math.sin(t * 2.5 + (x / s) * 1.3 + k) * s * 0.04);
    else { g.lineTo(x, top); g.lineTo(x + s, top); }
    g.lineTo(x + s, y + s); g.closePath(); g.fill();
    if (surface) {
      g.strokeStyle = 'rgba(255,255,255,0.75)'; g.lineWidth = Math.max(1, s * 0.05);
      g.beginPath(); for (let k = 0; k <= 4; k++) g.lineTo(x + (k / 4) * s, top + Math.sin(t * 2.5 + (x / s) * 1.3 + k) * s * 0.04); g.stroke();
    }
  }
  g.restore();
}

/** Lava in one tile: a glowing pool `d` deep (0–1), or a falling stream. */
export function drawLava(g: CanvasRenderingContext2D, x: number, y: number, s: number, d: number, fall: boolean, surface: boolean, t: number) {
  g.save();
  const top = y + s * (1 - d);
  const grad = g.createLinearGradient(0, top, 0, y + s);
  grad.addColorStop(0, '#ffb43a'); grad.addColorStop(1, '#e8401c');
  g.fillStyle = grad;
  if (fall) {
    g.fillRect(x + s * 0.08, y, s * 0.84, s);
    g.strokeStyle = 'rgba(255,240,160,0.75)'; g.lineWidth = s * 0.06;
    for (let k = 0; k < 3; k++) {
      const sx = x + s * (0.25 + k * 0.25), off = ((t * 2.2 + k * 0.37) % 1) * s;
      g.beginPath(); g.moveTo(sx, y + off - s * 0.3); g.lineTo(sx, y + off); g.stroke();
    }
  } else {
    g.beginPath(); g.moveTo(x, y + s);
    if (surface) {
      for (let k = 0; k <= 4; k++) g.lineTo(x + (k / 4) * s, top + Math.sin(t * 3 + (x / s) * 1.7 + k) * s * 0.03);
    } else { g.lineTo(x, top); g.lineTo(x + s, top); }
    g.lineTo(x + s, y + s); g.closePath(); g.fill();
    if (surface) {
      g.fillStyle = 'rgba(255,240,160,0.8)';
      const bx = x + s * (0.2 + 0.6 * ((Math.sin((x / s) * 12.9) + 1) / 2)), p = (t * 0.8 + x / s * 0.31) % 1;
      if (d > 0.3) { g.beginPath(); g.arc(bx, top + s * 0.1 + (1 - p) * d * s * 0.6, s * 0.06 * (1 - p), 0, Math.PI * 2); g.fill(); }
    }
  }
  g.restore();
}

export const LIMITS = { minW: 20, maxW: 120, minH: 10, maxH: 24 };

export function blankLevel(w = 60, h = 14): Level {
  const rows: string[] = [];
  for (let y = 0; y < h; y++) {
    let row = '';
    for (let x = 0; x < w; x++) row += y >= h - 2 ? 'g' : '.';
    rows.push(row);
  }
  const t = rows.join('').split('');
  t[(h - 3) * w + 2] = 'S';
  t[(h - 3) * w + w - 3] = 'G';
  return { v: 1, w, h, tiles: t.join(''), theme: 'meadow', gravity: 'normal', time: 0, allCoins: false };
}

export const tileAt = (l: Level, x: number, y: number) => (x < 0 || x >= l.w || y < 0 || y >= l.h ? '.' : l.tiles[y * l.w + x]);

export function setTile(l: Level, x: number, y: number, ch: string): Level {
  if (x < 0 || x >= l.w || y < 0 || y >= l.h) return l;
  const t = l.tiles.split('');
  if (ch === 'S') for (let i = 0; i < t.length; i++) if (t[i] === 'S') t[i] = '.';   // only one start
  t[y * l.w + x] = ch;
  return { ...l, tiles: t.join('') };
}

/** Make the level wider or narrower (on the right) or taller or shorter (at the top), keeping what's built. */
export function resize(l: Level, w: number, h: number): Level {
  w = Math.max(LIMITS.minW, Math.min(LIMITS.maxW, w));
  h = Math.max(LIMITS.minH, Math.min(LIMITS.maxH, h));
  const t: string[] = [];
  for (let y = 0; y < h; y++) {
    const sy = y - (h - l.h);                        // keep the floor at the bottom
    for (let x = 0; x < w; x++) t.push(tileAt(l, x, sy));
  }
  let out: Level = { ...l, w, h, tiles: t.join('') };
  if (!out.tiles.includes('S')) out = setTile(out, 1, Math.max(0, h - 3), 'S');
  return out;
}

/** What's wrong with a level, or '' if it's fine to play. */
export function checkLevel(l: Level): string {
  if (l.tiles.length !== l.w * l.h) return 'This level is broken. Start a new one.';
  if ((l.tiles.match(/S/g) ?? []).length !== 1) return 'Put one Start where players begin.';
  if (!l.tiles.includes('G')) return 'Put a Flag where players win.';
  if (l.allCoins && !l.tiles.includes('o')) return '“Collect every coin” is on, but there are no coins.';
  return '';
}

/** A level as it's sent to the database, and back (anything odd falls back to safe values). */
export function cleanLevel(x: unknown): Level | null {
  const v = (x && typeof x === 'object' ? x : null) as Record<string, unknown> | null;
  if (!v || typeof v.tiles !== 'string' || typeof v.w !== 'number' || typeof v.h !== 'number') return null;
  if (v.tiles.length !== v.w * v.h) return null;
  return {
    v: 1, w: v.w, h: v.h, tiles: v.tiles.replace(/[^.a-zA-Z]/g, '.'),
    theme: v.theme === 'desert' || v.theme === 'sunset' || v.theme === 'night' ? v.theme : 'meadow',
    gravity: v.gravity === 'floaty' ? 'floaty' : 'normal',
    time: typeof v.time === 'number' && v.time > 0 ? Math.min(600, v.time) : 0,
    allCoins: v.allCoins === true,
  };
}

// ── Drawing ─────────────────────────────────────────────────────────────────
const INK = '#1e2633';

export const THEMES: Record<Theme, { name: string; top: string; bottom: string; hill: string; hill2: string }> = {
  meadow: { name: '🌳 Meadow', top: '#7cc8ff', bottom: '#d8f1ff', hill: '#8fd47a', hill2: '#6bbf5c' },
  desert: { name: '🌵 Desert', top: '#ffd58a', bottom: '#fff1d0', hill: '#f2c27a', hill2: '#e0a85c' },
  sunset: { name: '🌅 Sunset', top: '#ff9a6b', bottom: '#ffd7a8', hill: '#b0779e', hill2: '#8a5a84' },
  night: { name: '🌙 Night', top: '#0c1638', bottom: '#2a3a6e', hill: '#24325e', hill2: '#1a2448' },
};

/** Sky, sun or moon, clouds and two rows of hills, moving slower than the level (parallax). */
export function drawBackground(g: CanvasRenderingContext2D, theme: Theme, W: number, H: number, camX: number, t: number) {
  const th = THEMES[theme];
  const grad = g.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, th.top); grad.addColorStop(1, th.bottom);
  g.fillStyle = grad; g.fillRect(0, 0, W, H);
  if (theme === 'night') {
    g.fillStyle = '#fff';
    for (let i = 0; i < 40; i++) { const x = ((i * 97 - camX * 0.05) % W + W) % W, y = (i * 53) % (H * 0.6); g.globalAlpha = 0.4 + 0.6 * Math.abs(Math.sin(t + i)); g.fillRect(x, y, 2, 2); }
    g.globalAlpha = 1;
    g.fillStyle = '#fdf6d8'; g.beginPath(); g.arc(W * 0.8, H * 0.18, H * 0.07, 0, Math.PI * 2); g.fill();
  } else {
    g.fillStyle = theme === 'sunset' ? '#ffef9a' : '#fff3a0';
    g.beginPath(); g.arc(W * 0.82, H * (theme === 'sunset' ? 0.42 : 0.16), H * 0.08, 0, Math.PI * 2); g.fill();
  }
  // Clouds.
  g.fillStyle = theme === 'night' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.85)';
  for (let i = 0; i < 5; i++) {
    const x = ((i * 330 - camX * 0.15 + t * 6) % (W + 300) + W + 300) % (W + 300) - 150, y = H * (0.1 + (i % 3) * 0.09);
    for (const [dx, dy, r] of [[0, 0, 26], [28, -10, 30], [58, 0, 24]]) { g.beginPath(); g.arc(x + dx, y + dy, r, 0, Math.PI * 2); g.fill(); }
  }
  // Hills.
  for (const [col, k, base, amp] of [[th.hill, 0.25, 0.62, 0.1], [th.hill2, 0.45, 0.74, 0.08]] as [string, number, number, number][]) {
    g.fillStyle = col; g.beginPath(); g.moveTo(0, H);
    for (let x = 0; x <= W; x += 16) g.lineTo(x, H * base - Math.sin((x + camX * k) / 140) * H * amp - Math.sin((x + camX * k) / 57) * H * amp * 0.3);
    g.lineTo(W, H); g.closePath(); g.fill();
  }
}

/** One tile at (x, y), size s px. `t` animates coins, seaweed and the flag. `open` draws a door as opened. */
export function drawTile(g: CanvasRenderingContext2D, ch: string, x: number, y: number, s: number, t = 0, open = false) {
  const lw = Math.max(1, s / 16);
  g.save();
  g.lineWidth = lw; g.strokeStyle = INK; g.lineJoin = 'round';
  const block = (fill: string, top?: string) => {
    g.fillStyle = fill; g.fillRect(x, y, s, s);
    if (top) { g.fillStyle = top; g.fillRect(x, y, s, s * 0.22); }
    g.strokeStyle = 'rgba(30,38,51,0.35)'; g.strokeRect(x + lw / 2, y + lw / 2, s - lw, s - lw);
  };
  switch (ch) {
    case 'g': block('#9b6b43', '#5cc24f'); g.fillStyle = '#4aa83f'; for (let k = 0; k < 4; k++) { g.beginPath(); g.moveTo(x + s * (k / 4), y + s * 0.2); g.lineTo(x + s * (k / 4 + 0.125), y + s * 0.32); g.lineTo(x + s * ((k + 1) / 4), y + s * 0.2); g.fill(); } g.fillStyle = '#7d5434'; for (const [a, b] of [[0.25, 0.55], [0.7, 0.7], [0.45, 0.85]]) { g.beginPath(); g.arc(x + s * a, y + s * b, s * 0.05, 0, Math.PI * 2); g.fill(); } break;
    case 's': block('#8792a3'); g.strokeStyle = 'rgba(30,38,51,0.3)'; g.beginPath(); g.moveTo(x + s * 0.2, y + s * 0.3); g.lineTo(x + s * 0.5, y + s * 0.45); g.lineTo(x + s * 0.45, y + s * 0.8); g.stroke(); break;
    case 'b': block('#c8563f'); g.strokeStyle = '#8f3626'; g.beginPath(); g.moveTo(x, y + s / 2); g.lineTo(x + s, y + s / 2); g.moveTo(x + s / 2, y); g.lineTo(x + s / 2, y + s / 2); g.moveTo(x + s / 4, y + s / 2); g.lineTo(x + s / 4, y + s); g.moveTo(x + s * 0.75, y + s / 2); g.lineTo(x + s * 0.75, y + s); g.stroke(); break;
    case 'c': block('#d9a05b'); g.strokeStyle = '#9a6a33'; g.lineWidth = lw * 1.5; g.strokeRect(x + s * 0.1, y + s * 0.1, s * 0.8, s * 0.8); g.beginPath(); g.moveTo(x + s * 0.1, y + s * 0.1); g.lineTo(x + s * 0.9, y + s * 0.9); g.moveTo(x + s * 0.9, y + s * 0.1); g.lineTo(x + s * 0.1, y + s * 0.9); g.stroke(); break;
    case 'i': block('#bfefff', '#e6fbff'); g.strokeStyle = 'rgba(255,255,255,0.9)'; g.beginPath(); g.moveTo(x + s * 0.2, y + s * 0.7); g.lineTo(x + s * 0.45, y + s * 0.4); g.stroke(); break;
    case 'p':
      g.fillStyle = '#a0703f'; g.fillRect(x, y, s, s * 0.28); g.strokeRect(x + lw / 2, y + lw / 2, s - lw, s * 0.28);
      g.fillStyle = '#c48d55'; g.fillRect(x + lw, y + lw, s - 2 * lw, s * 0.08); break;
    case 'm': case 'v': drawPlatform(g, x, y, s, s, ch === 'm' ? 'x' : 'y', true); break;
    case 'x':
      g.fillStyle = '#c9d2de';
      for (let k = 0; k < 3; k++) { g.beginPath(); g.moveTo(x + (k / 3) * s, y + s); g.lineTo(x + ((k + 0.5) / 3) * s, y + s * 0.35); g.lineTo(x + ((k + 1) / 3) * s, y + s); g.closePath(); g.fill(); g.stroke(); }
      break;
    case 'u': {
      g.fillStyle = '#3fae5a';
      g.beginPath(); g.roundRect(x + s * 0.38, y + s * 0.15, s * 0.24, s * 0.85, s * 0.12); g.fill(); g.stroke();
      g.beginPath(); g.roundRect(x + s * 0.14, y + s * 0.38, s * 0.14, s * 0.3, s * 0.07); g.fill(); g.stroke();
      g.beginPath(); g.roundRect(x + s * 0.72, y + s * 0.28, s * 0.14, s * 0.3, s * 0.07); g.fill(); g.stroke();
      g.fillRect(x + s * 0.26, y + s * 0.6, s * 0.14, s * 0.08); g.fillRect(x + s * 0.6, y + s * 0.5, s * 0.14, s * 0.08);
      g.strokeStyle = '#fff'; g.lineWidth = lw * 0.8;
      for (const [a, b] of [[0.4, 0.3], [0.6, 0.5], [0.42, 0.7], [0.58, 0.85]]) { g.beginPath(); g.moveTo(x + s * a, y + s * b); g.lineTo(x + s * (a + (a < 0.5 ? -0.08 : 0.08)), y + s * (b - 0.04)); g.stroke(); }
      break;
    }
    case 'l': drawLava(g, x, y, s, 1, false, true, t); break;
    case 'a': drawWater(g, x, y, s, 1, false, true, t); break;
    case 'j':
      g.fillStyle = '#9aa4b2';
      g.beginPath(); for (let k = 0; k <= 4; k++) g.lineTo(x + s * (k % 2 ? 0.75 : 0.25), y + s * (0.95 - k * 0.12)); g.stroke();
      g.fillStyle = '#ff5c5c'; g.fillRect(x + s * 0.12, y + s * 0.38, s * 0.76, s * 0.16); g.strokeRect(x + s * 0.12, y + s * 0.38, s * 0.76, s * 0.16);
      break;
    case 'o': {
      const w = Math.abs(Math.cos(t * 3)) * 0.8 + 0.2;
      g.fillStyle = '#ffd23d'; g.beginPath(); g.ellipse(x + s / 2, y + s / 2, s * 0.3 * w, s * 0.3, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      g.fillStyle = '#fff6a8'; g.beginPath(); g.ellipse(x + s / 2 - s * 0.08 * w, y + s * 0.42, s * 0.06 * w, s * 0.1, 0, 0, Math.PI * 2); g.fill();
      break;
    }
    case 'k':
      g.fillStyle = '#ffd23d';
      g.beginPath(); g.arc(x + s * 0.35, y + s * 0.4, s * 0.17, 0, Math.PI * 2); g.fill(); g.stroke();
      g.fillRect(x + s * 0.45, y + s * 0.36, s * 0.42, s * 0.1); g.strokeRect(x + s * 0.45, y + s * 0.36, s * 0.42, s * 0.1);
      g.fillRect(x + s * 0.72, y + s * 0.46, s * 0.08, s * 0.14);
      g.fillStyle = '#1e2633'; g.beginPath(); g.arc(x + s * 0.35, y + s * 0.4, s * 0.06, 0, Math.PI * 2); g.fill();
      break;
    case 'd':
      if (open) { g.strokeStyle = 'rgba(30,38,51,0.4)'; g.setLineDash([s / 8, s / 8]); g.strokeRect(x + lw, y + lw, s - 2 * lw, s - 2 * lw); break; }
      g.fillStyle = '#7a4a24'; g.fillRect(x, y, s, s); g.strokeRect(x + lw / 2, y + lw / 2, s - lw, s - lw);
      g.fillStyle = '#ffd23d'; g.beginPath(); g.arc(x + s / 2, y + s * 0.45, s * 0.1, 0, Math.PI * 2); g.fill();
      g.fillRect(x + s * 0.46, y + s * 0.5, s * 0.08, s * 0.18);
      break;
    case 'w': {
      g.strokeStyle = '#3fae5a'; g.lineWidth = s * 0.06;
      for (const [ox, hh, col] of [[0.3, 0.55, '#ff6fa8'], [0.62, 0.42, '#ffd23d']] as [number, number, string][]) {
        const sway = Math.sin(t * 2 + ox * 5) * s * 0.04;
        g.beginPath(); g.moveTo(x + s * ox, y + s); g.lineTo(x + s * ox + sway, y + s * (1 - hh)); g.stroke();
        g.fillStyle = col;
        for (let k = 0; k < 5; k++) { const a = (k / 5) * Math.PI * 2; g.beginPath(); g.arc(x + s * ox + sway + Math.cos(a) * s * 0.08, y + s * (1 - hh) + Math.sin(a) * s * 0.08, s * 0.065, 0, Math.PI * 2); g.fill(); }
        g.fillStyle = '#fff'; g.beginPath(); g.arc(x + s * ox + sway, y + s * (1 - hh), s * 0.05, 0, Math.PI * 2); g.fill();
      }
      break;
    }
    case 'r': drawCrab(g, x + s / 2, y + s * 0.62, s, t, 1); break;
    case 'f': drawBee(g, x + s / 2, y + s / 2, s, t); break;
    case 'n': drawCannon(g, x, y, s, 0, 0); break;
    case 'y': case 't': case 'e': case 'z': drawPowerup(g, ch, x + s / 2, y + s / 2, s, t); break;
    case 'q': drawRobot(g, x + s / 2, y + s * 0.55, s, t, 1); break;
    case 'B': drawBoss(g, x + s / 2, y + s, s * 0.45, t, 1, false, false); break;
    case 'h': drawCheckpoint(g, x, y, s, false); break;
    case 'S':
      g.fillStyle = 'rgba(47,207,106,0.25)'; g.fillRect(x, y, s, s);
      g.fillStyle = '#2fcf6a'; g.font = `bold ${s * 0.5}px system-ui`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('▶', x + s / 2, y + s / 2);
      break;
    case 'G': {
      g.fillStyle = '#e8e8e8'; g.fillRect(x + s * 0.18, y + s * 0.05, s * 0.08, s * 0.95); g.strokeRect(x + s * 0.18, y + s * 0.05, s * 0.08, s * 0.95);
      const wave = Math.sin(t * 5) * s * 0.06;
      g.fillStyle = '#2fcf6a';
      g.beginPath(); g.moveTo(x + s * 0.26, y + s * 0.08); g.quadraticCurveTo(x + s * 0.55, y + s * 0.08 + wave, x + s * 0.88, y + s * 0.22);
      g.quadraticCurveTo(x + s * 0.55, y + s * 0.36 - wave, x + s * 0.26, y + s * 0.42); g.closePath(); g.fill(); g.stroke();
      break;
    }
  }
  g.restore();
}

export function drawCheckpoint(g: CanvasRenderingContext2D, x: number, y: number, s: number, lit: boolean) {
  g.save();
  g.lineWidth = Math.max(1, s / 16); g.strokeStyle = INK;
  g.fillStyle = '#c9d2de'; g.fillRect(x + s * 0.44, y + s * 0.2, s * 0.12, s * 0.8);
  g.fillStyle = lit ? '#ffd23d' : '#8a94a3';
  if (lit) { g.shadowColor = '#ffd23d'; g.shadowBlur = s * 0.5; }
  g.beginPath(); g.arc(x + s / 2, y + s * 0.22, s * 0.18, 0, Math.PI * 2); g.fill(); g.shadowBlur = 0; g.stroke();
  g.restore();
}

export function drawCrab(g: CanvasRenderingContext2D, cx: number, cy: number, s: number, t: number, dir: number) {
  g.save();
  g.lineWidth = Math.max(1, s / 16); g.strokeStyle = INK;
  const step = Math.sin(t * 12) * s * 0.04;
  g.strokeStyle = '#c23b22'; g.lineWidth = s * 0.06;
  for (const k of [-1, 1]) for (const j of [0.12, 0.24]) { g.beginPath(); g.moveTo(cx + k * s * j, cy); g.lineTo(cx + k * s * (j + 0.14), cy + s * 0.28 + (j === 0.12 ? step : -step)); g.stroke(); }
  g.lineWidth = Math.max(1, s / 16); g.strokeStyle = INK;
  g.fillStyle = '#ff6b4a'; g.beginPath(); g.ellipse(cx, cy, s * 0.36, s * 0.22, 0, 0, Math.PI * 2); g.fill(); g.stroke();
  for (const k of [-1, 1]) { g.beginPath(); g.arc(cx + k * s * 0.42, cy - s * 0.14, s * 0.11, 0, Math.PI * 2); g.fill(); g.stroke(); }
  g.fillStyle = '#fff';
  for (const k of [-1, 1]) { g.beginPath(); g.arc(cx + k * s * 0.12 + dir * s * 0.03, cy - s * 0.28, s * 0.07, 0, Math.PI * 2); g.fill(); g.stroke(); }
  g.fillStyle = INK;
  for (const k of [-1, 1]) { g.beginPath(); g.arc(cx + k * s * 0.12 + dir * s * 0.05, cy - s * 0.28, s * 0.03, 0, Math.PI * 2); g.fill(); }
  g.restore();
}

export function drawBee(g: CanvasRenderingContext2D, cx: number, cy: number, s: number, t: number) {
  g.save();
  g.lineWidth = Math.max(1, s / 16); g.strokeStyle = INK;
  const flap = Math.abs(Math.sin(t * 30));
  g.fillStyle = 'rgba(255,255,255,0.8)';
  for (const k of [-1, 1]) { g.beginPath(); g.ellipse(cx + k * s * 0.1, cy - s * 0.22, s * 0.1, s * (0.08 + flap * 0.1), k * 0.4, 0, Math.PI * 2); g.fill(); g.stroke(); }
  g.fillStyle = '#ffd23d'; g.beginPath(); g.ellipse(cx, cy, s * 0.3, s * 0.22, 0, 0, Math.PI * 2); g.fill();
  g.save(); g.clip(); g.fillStyle = INK; for (const k of [-0.1, 0.08]) g.fillRect(cx + k * s, cy - s * 0.3, s * 0.08, s * 0.6); g.restore();
  g.beginPath(); g.ellipse(cx, cy, s * 0.3, s * 0.22, 0, 0, Math.PI * 2); g.stroke();
  g.fillStyle = INK; g.beginPath(); g.arc(cx + s * 0.18, cy - s * 0.05, s * 0.035, 0, Math.PI * 2); g.fill();
  g.beginPath(); g.moveTo(cx - s * 0.3, cy); g.lineTo(cx - s * 0.4, cy + s * 0.04); g.lineTo(cx - s * 0.3, cy + s * 0.06); g.fill();
  g.restore();
}

/** A small picture of a whole level (for the teacher's review and the maker's list). */
export function levelThumb(l: Level, maxW = 360): string {
  const s = Math.max(2, Math.floor(Math.min(maxW / l.w, 8)));
  const c = document.createElement('canvas');
  c.width = l.w * s; c.height = l.h * s;
  const g = c.getContext('2d')!;
  drawBackground(g, l.theme, c.width, c.height, 0, 0);
  for (let y = 0; y < l.h; y++) for (let x = 0; x < l.w; x++) { const ch = l.tiles[y * l.w + x]; if (ch !== '.' && ch !== 'l' && ch !== 'a') drawTile(g, ch, x * s, y * s, s); }
  drawLiquid(g, l, computeLiquid(l), s, 0, 0, l.w - 1, 0, l.h - 1);
  return c.toDataURL('image/png');
}

/** The player's ride, standing on the ground at (cx, bottom), size s px per tile. */
export type GlobeDraw = {
  cx: number; bottom: number; s: number; t: number;
  facing: 1 | -1;
  /** Wheel turn, radians (grows with distance rolled). */
  roll: number;
  /** Tiles per second, for the water's slosh. */
  vx: number; vy: number;
  /** Squash on landing (0 none … 1 lots). */
  squash: number;
  /** Flash when hurt. */
  hurt?: boolean;
};

/**
 * A fish in an arch-shaped glass bowl on a little wheeled cart: the body is
 * GLOBE_W x GLOBE_H tiles (the player's hitbox), the wheels under it.
 */
export const GLOBE_W = 0.8, GLOBE_H = 0.95;
export function drawGlobe(g: CanvasRenderingContext2D, look: Look, o: GlobeDraw) {
  const { s, t } = o;
  const w = GLOBE_W * s, h = GLOBE_H * s;
  const sq = Math.min(1, o.squash);
  g.save();
  g.translate(o.cx, o.bottom);
  g.scale(1 + sq * 0.12, 1 - sq * 0.12);
  g.lineJoin = 'round'; g.lineCap = 'round';
  const lw = Math.max(1.2, s / 18);
  g.lineWidth = lw; g.strokeStyle = INK;

  // Wheels.
  const wr = s * 0.13, wy = -wr;
  for (const wx of [-w * 0.3, w * 0.3]) {
    g.fillStyle = '#2c2f3a'; g.beginPath(); g.arc(wx, wy, wr, 0, Math.PI * 2); g.fill(); g.stroke();
    g.fillStyle = '#c9d2de'; g.beginPath(); g.arc(wx, wy, wr * 0.45, 0, Math.PI * 2); g.fill();
    g.strokeStyle = '#c9d2de'; g.lineWidth = lw * 0.8;
    for (let k = 0; k < 3; k++) { const a = o.roll + (k * Math.PI * 2) / 3; g.beginPath(); g.moveTo(wx, wy); g.lineTo(wx + Math.cos(a) * wr * 0.85, wy + Math.sin(a) * wr * 0.85); g.stroke(); }
    g.strokeStyle = INK; g.lineWidth = lw;
  }
  // The cart the bowl sits in.
  const cartTop = -wr * 1.4 - s * 0.12;
  g.fillStyle = '#ff8a3d';
  g.beginPath(); g.roundRect(-w / 2, cartTop, w, s * 0.14, s * 0.05); g.fill(); g.stroke();

  // The bowl: an arch (straight sides, round top).
  const bh = h - (-cartTop), r = w / 2;
  const top = cartTop - bh;
  const arch = new Path2D();
  arch.moveTo(-r, cartTop); arch.lineTo(-r, top + r); arch.arc(0, top + r, r, Math.PI, 0); arch.lineTo(r, cartTop); arch.closePath();
  g.fillStyle = o.hurt ? 'rgba(255,90,90,0.35)' : 'rgba(210,240,255,0.35)'; g.fill(arch);

  // Water, tilting with speed and sloshing.
  g.save(); g.clip(arch);
  const tilt = Math.max(-0.35, Math.min(0.35, -o.vx * 0.04 + Math.sin(t * 4) * 0.03));
  const level = cartTop - bh * (0.62 + Math.max(-0.1, Math.min(0.1, o.vy * 0.01)));
  g.fillStyle = 'rgba(70,170,255,0.55)';
  g.beginPath(); g.moveTo(-r - 2, level - tilt * r); g.lineTo(r + 2, level + tilt * r); g.lineTo(r + 2, cartTop + 2); g.lineTo(-r - 2, cartTop + 2); g.closePath(); g.fill();
  g.strokeStyle = 'rgba(255,255,255,0.7)'; g.beginPath(); g.moveTo(-r, level - tilt * r); g.lineTo(r, level + tilt * r); g.stroke();
  // The fish, swimming in place.
  drawAvatar(g, look, { x: 0, y: cartTop - bh * 0.36 + Math.sin(t * 3) * s * 0.03, size: w * 0.66, facing: o.facing, effort: Math.min(1, Math.abs(o.vx) / 6 + 0.3), tilt: Math.max(-0.4, Math.min(0.4, o.vy * 0.03)) * o.facing, t });
  // Bubbles.
  g.fillStyle = 'rgba(255,255,255,0.8)';
  for (let k = 0; k < 3; k++) { const p = (t * 0.7 + k / 3) % 1; g.beginPath(); g.arc(-r * 0.4 + k * r * 0.35, cartTop - p * bh * 0.6, s * 0.025, 0, Math.PI * 2); g.fill(); }
  g.restore();

  // Glass outline and shine.
  g.strokeStyle = INK; g.stroke(arch);
  g.strokeStyle = 'rgba(255,255,255,0.85)'; g.lineWidth = lw * 1.6;
  g.beginPath(); g.arc(0, top + r, r * 0.72, Math.PI * 1.15, Math.PI * 1.45); g.stroke();
  g.restore();
}

// ── Moving platforms ─────────────────────────────────────────────────────────

export type Platform = {
  /** Where it is now (its top-left corner, in tiles), how wide, and which way it moves. */
  x: number; y: number; w: number;
  axis: 'x' | 'y';
  dir: 1 | -1;
  /** How far it may go (the left/top position limits). */
  min: number; max: number;
};
export const PLATFORM_SPEED = 2;   // tiles a second
const PLATFORM_RANGE = 6;          // at most this far each way from where it was painted

/**
 * The moving platforms in a level: each run of 'm' (sliders) or 'v' (lifts) in
 * a row is one platform. A slider travels sideways until a solid tile (or the
 * level's edge) is in the way; a lift up and down the same way.
 */
export function findPlatforms(l: { w: number; h: number; tiles: string }): Platform[] {
  const { w, h, tiles } = l;
  const solidAt = (x: number, y: number) => x < 0 || x >= w || y < 0 || y >= h || SOLID.has(tiles[y * w + x]) || tiles[y * w + x] === 'd';
  const out: Platform[] = [];
  for (let y = 0; y < h; y++) {
    let x = 0;
    while (x < w) {
      const ch = tiles[y * w + x];
      if (ch !== 'm' && ch !== 'v') { x++; continue; }
      let len = 1;
      while (x + len < w && tiles[y * w + x + len] === ch) len++;
      if (ch === 'm') {
        let lo = x, hi = x;
        while (lo > x - PLATFORM_RANGE && !solidAt(lo - 1, y)) lo--;
        while (hi < x + PLATFORM_RANGE && !solidAt(hi + len, y)) hi++;
        out.push({ x, y, w: len, axis: 'x', dir: 1, min: lo, max: hi });
      } else {
        const clear = (yy: number) => { for (let k = 0; k < len; k++) if (solidAt(x + k, yy)) return false; return true; };
        let lo = y, hi = y;
        while (lo > y - PLATFORM_RANGE && lo > 1 && clear(lo - 1)) lo--;
        while (hi < y + PLATFORM_RANGE && hi < h - 1 && clear(hi + 1)) hi++;
        out.push({ x, y, w: len, axis: 'y', dir: -1, min: lo, max: hi });
      }
      x += len;
    }
  }
  return out;
}

/** A moving platform `wPx` wide at (x, y): metal with yellow-and-black ends and arrows showing which way it goes. */
export function drawPlatform(g: CanvasRenderingContext2D, x: number, y: number, wPx: number, s: number, axis: 'x' | 'y', arrows: boolean) {
  g.save();
  const th = s * 0.3, lw = Math.max(1, s / 16);
  g.fillStyle = '#7d8aa0'; g.fillRect(x, y, wPx, th);
  g.fillStyle = '#a9b4c6'; g.fillRect(x + lw, y + lw, wPx - 2 * lw, th * 0.3);
  g.strokeStyle = INK; g.lineWidth = lw; g.strokeRect(x + lw / 2, y + lw / 2, wPx - lw, th - lw / 2);
  // Hazard stripes at the ends.
  g.save(); g.beginPath(); g.rect(x, y, s * 0.22, th); g.rect(x + wPx - s * 0.22, y, s * 0.22, th); g.clip();
  g.fillStyle = '#ffd23d'; g.fillRect(x, y, wPx, th);
  g.strokeStyle = INK; g.lineWidth = s * 0.06;
  for (let k = -2; k < wPx / (s * 0.15) + 2; k++) { g.beginPath(); g.moveTo(x + k * s * 0.15, y + th); g.lineTo(x + k * s * 0.15 + th, y); g.stroke(); }
  g.restore();
  if (arrows) {
    g.fillStyle = 'rgba(30,38,51,0.55)'; g.font = `bold ${s * 0.42}px system-ui`; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillText(axis === 'x' ? '↔' : '↕', x + wPx / 2, y + th + s * 0.3);
  }
  g.restore();
}

// ── Creatures that shoot ─────────────────────────────────────────────────────

/** A cannon on its base, its barrel turned to `angle` (radians, 0 = right). `kick` (0–1) is the recoil after a shot. */
export function drawCannon(g: CanvasRenderingContext2D, x: number, y: number, s: number, angle: number, kick: number) {
  g.save();
  const lw = Math.max(1, s / 16);
  g.lineWidth = lw; g.strokeStyle = INK; g.lineJoin = 'round';
  // The base: a solid stone block with a dark top.
  g.fillStyle = '#6b7484'; g.fillRect(x, y + s * 0.45, s, s * 0.55); g.strokeRect(x + lw / 2, y + s * 0.45, s - lw, s * 0.55 - lw / 2);
  g.fillStyle = '#4a5161'; g.beginPath(); g.arc(x + s / 2, y + s * 0.47, s * 0.3, Math.PI, 0); g.fill(); g.stroke();
  // The barrel.
  g.translate(x + s / 2, y + s * 0.42); g.rotate(angle); g.translate(-kick * s * 0.12, 0);
  g.fillStyle = '#2c2f3a'; g.beginPath(); g.roundRect(-s * 0.05, -s * 0.14, s * 0.58, s * 0.28, s * 0.06); g.fill(); g.stroke();
  g.fillStyle = '#ff5c5c'; g.fillRect(s * 0.42, -s * 0.15, s * 0.07, s * 0.3);
  g.restore();
}

/** A little robot walking; `dir` 1 faces right. `charge` (0–1) lights its eye before it fires. */
export function drawRobot(g: CanvasRenderingContext2D, cx: number, cy: number, s: number, t: number, dir: number, charge = 0) {
  g.save();
  const lw = Math.max(1, s / 16);
  g.lineWidth = lw; g.strokeStyle = INK; g.lineJoin = 'round';
  g.translate(cx, cy); g.scale(dir, 1);
  const step = Math.sin(t * 10) * s * 0.05;
  // Legs, body, antenna, eye, blaster arm.
  g.fillStyle = '#5b6b7f';
  g.fillRect(-s * 0.2, s * 0.18 + step, s * 0.1, s * 0.22); g.fillRect(s * 0.1, s * 0.18 - step, s * 0.1, s * 0.22);
  g.fillStyle = '#c9d2de'; g.beginPath(); g.roundRect(-s * 0.3, -s * 0.28, s * 0.6, s * 0.5, s * 0.1); g.fill(); g.stroke();
  g.beginPath(); g.moveTo(0, -s * 0.28); g.lineTo(0, -s * 0.42); g.stroke();
  g.fillStyle = '#ff5c5c'; g.beginPath(); g.arc(0, -s * 0.44, s * 0.05, 0, Math.PI * 2); g.fill();
  g.fillStyle = '#1e2633'; g.beginPath(); g.roundRect(-s * 0.05, -s * 0.18, s * 0.3, s * 0.14, s * 0.05); g.fill();
  g.fillStyle = charge > 0 ? `rgba(255,${Math.round(200 - charge * 150)},60,1)` : '#4fe3ff';
  if (charge > 0) { g.shadowColor = '#ff7a3d'; g.shadowBlur = s * 0.3 * charge; }
  g.beginPath(); g.arc(s * 0.13, -s * 0.11, s * 0.045, 0, Math.PI * 2); g.fill(); g.shadowBlur = 0;
  g.fillStyle = '#5b6b7f'; g.fillRect(s * 0.22, s * 0.0, s * 0.18, s * 0.09); g.strokeRect(s * 0.22, s * 0.0, s * 0.18, s * 0.09);
  g.restore();
}

/** A cannonball or a laser bolt at (cx, cy), moving with (vx, vy). */
export function drawShot(g: CanvasRenderingContext2D, cx: number, cy: number, s: number, kind: 'ball' | 'laser' | 'rock' | 'wave', vx: number, vy: number) {
  g.save();
  if (kind === 'rock') {
    g.fillStyle = '#8a6a4a'; g.strokeStyle = INK; g.lineWidth = Math.max(1, s / 16);
    g.beginPath(); for (let k = 0; k < 7; k++) { const a = (k / 7) * Math.PI * 2, r = s * (0.18 + (k % 2) * 0.04); g.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r); } g.closePath(); g.fill(); g.stroke();
  } else if (kind === 'wave') {
    // A shockwave rolling along the ground: a curl of dust, (cx, cy) at its middle.
    const dir = Math.sign(vx) || 1;
    g.fillStyle = 'rgba(210,180,140,0.85)'; g.strokeStyle = 'rgba(120,90,60,0.9)'; g.lineWidth = Math.max(1, s / 16);
    g.beginPath(); g.moveTo(cx - dir * s * 0.4, cy + s * 0.25); g.quadraticCurveTo(cx, cy - s * 0.45, cx + dir * s * 0.3, cy + s * 0.25); g.closePath(); g.fill(); g.stroke();
  } else if (kind === 'ball') {
    g.fillStyle = '#2c2f3a'; g.strokeStyle = INK; g.lineWidth = Math.max(1, s / 16);
    g.beginPath(); g.arc(cx, cy, s * 0.17, 0, Math.PI * 2); g.fill();
    g.fillStyle = 'rgba(255,255,255,0.5)'; g.beginPath(); g.arc(cx - s * 0.05, cy - s * 0.05, s * 0.05, 0, Math.PI * 2); g.fill();
  } else {
    const a = Math.atan2(vy, vx);
    g.translate(cx, cy); g.rotate(a);
    g.shadowColor = '#ff3b3b'; g.shadowBlur = s * 0.3;
    g.fillStyle = '#ff6a5c'; g.beginPath(); g.roundRect(-s * 0.25, -s * 0.06, s * 0.5, s * 0.12, s * 0.06); g.fill();
    g.fillStyle = '#fff3e0'; g.fillRect(-s * 0.18, -s * 0.02, s * 0.36, s * 0.04);
  }
  g.restore();
}

// ── Power-ups ────────────────────────────────────────────────────────────────

export const POWERUPS: Record<string, { name: string; icon: string; secs: number; color: string }> = {
  y: { name: 'Shield', icon: '🛡️', secs: 0, color: '#4fc3ff' },     // lasts until it blocks a hit
  t: { name: 'Star', icon: '⭐', secs: 8, color: '#ffd23d' },
  e: { name: 'Wings', icon: '🪽', secs: 15, color: '#b98cff' },
  z: { name: 'Rocket', icon: '⚡', secs: 10, color: '#ff8a3d' },
};
/** Seconds before a taken power-up comes back. */
export const POWERUP_RESPAWN = 8;

/** A power-up floating in a bubble, centred on (cx, cy). */
export function drawPowerup(g: CanvasRenderingContext2D, ch: string, cx: number, cy: number, s: number, t: number) {
  const p = POWERUPS[ch]; if (!p) return;
  g.save();
  const y = cy + Math.sin(t * 3 + cx) * s * 0.06;
  g.shadowColor = p.color; g.shadowBlur = s * 0.35;
  g.fillStyle = p.color + '88'; g.strokeStyle = 'rgba(255,255,255,0.9)'; g.lineWidth = Math.max(1, s / 18);   // tinted in the power-up's colour
  g.beginPath(); g.arc(cx, y, s * 0.36, 0, Math.PI * 2); g.fill(); g.stroke();
  g.shadowBlur = 0;
  g.font = `${s * 0.42}px system-ui, "Apple Color Emoji", "Segoe UI Emoji"`; g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillStyle = '#000';                 // opaque, or the emoji comes out faded
  g.fillText(p.icon, cx, y + s * 0.02);
  g.fillStyle = 'rgba(255,255,255,0.8)'; g.beginPath(); g.arc(cx - s * 0.14, y - s * 0.16, s * 0.06, 0, Math.PI * 2); g.fill();
  g.restore();
}

/** What a power-up looks like on the player's bowl (drawn over it). (cx, bottom) as for drawGlobe. */
export function drawPowerAura(g: CanvasRenderingContext2D, cx: number, bottom: number, s: number, t: number, a: { shield: boolean; star: number; wings: number; speed: number; facing: number }) {
  g.save();
  const midY = bottom - GLOBE_H * s * 0.5;
  if (a.speed > 0) {                       // flames from behind the cart
    for (let k = 0; k < 3; k++) {
      const fl = s * (0.25 + 0.15 * Math.sin(t * 30 + k * 2));
      g.fillStyle = k === 1 ? '#ffd23d' : '#ff6b2d';
      g.beginPath(); const bx = cx - a.facing * GLOBE_W * s * 0.5, by = bottom - s * (0.22 + k * 0.06);
      g.moveTo(bx, by - s * 0.05); g.lineTo(bx - a.facing * fl, by); g.lineTo(bx, by + s * 0.05); g.closePath(); g.fill();
    }
  }
  if (a.wings > 0) {                       // little wings on the cart
    const flap = Math.sin(t * 14) * 0.4;
    g.fillStyle = '#fff'; g.strokeStyle = INK; g.lineWidth = Math.max(1, s / 18);
    for (const side of [-1, 1]) {
      g.save(); g.translate(cx + side * GLOBE_W * s * 0.45, bottom - s * 0.35); g.rotate(side * (-0.5 + flap));
      g.beginPath(); g.ellipse(side * s * 0.18, -s * 0.05, s * 0.2, s * 0.09, 0, 0, Math.PI * 2); g.fill(); g.stroke();
      g.restore();
    }
  }
  if (a.star > 0) {                        // rainbow sparkle, flickering as it runs out
    if (a.star > 2 || Math.floor(t * 10) % 2) {
      g.globalAlpha = 0.45; g.fillStyle = `hsl(${(t * 360) % 360}, 90%, 60%)`;
      g.beginPath(); g.ellipse(cx, midY, GLOBE_W * s * 0.75, GLOBE_H * s * 0.62, 0, 0, Math.PI * 2); g.fill();
      g.globalAlpha = 1;
    }
  }
  if (a.shield) {                          // a bubble
    g.strokeStyle = 'rgba(79,195,255,0.9)'; g.lineWidth = Math.max(2, s / 12);
    g.fillStyle = 'rgba(79,195,255,0.15)';
    g.beginPath(); g.ellipse(cx, midY, GLOBE_W * s * 0.8 + Math.sin(t * 4) * s * 0.02, GLOBE_H * s * 0.68, 0, 0, Math.PI * 2); g.fill(); g.stroke();
  }
  g.restore();
}

// ── Bosses ───────────────────────────────────────────────────────────────────

export const BOSS = { name: 'King Crab', hp: 5, w: 2.4, h: 1.7 };

/**
 * The King Crab: a giant crowned crab, `BOSS.w` x `BOSS.h` tiles, standing on
 * (cx, bottom). `s` is pixels per tile. Red flash when hurt; steam when angry.
 */
export function drawBoss(g: CanvasRenderingContext2D, cx: number, bottom: number, s: number, t: number, dir: number, hurt: boolean, angry: boolean) {
  g.save();
  const lw = Math.max(1.5, s / 14);
  g.lineWidth = lw; g.strokeStyle = INK; g.lineJoin = 'round'; g.lineCap = 'round';
  const W = BOSS.w * s, H = BOSS.h * s;
  const cy = bottom - H * 0.42;
  const body = hurt ? '#ffffff' : angry ? '#e8401c' : '#ff6b4a';
  const step = Math.sin(t * (angry ? 16 : 10)) * s * 0.06;
  // Legs.
  g.strokeStyle = '#a3301c'; g.lineWidth = s * 0.12;
  for (const k of [-1, 1]) for (const j of [0.15, 0.3, 0.45]) {
    g.beginPath(); g.moveTo(cx + k * W * j * 0.8, cy + H * 0.1);
    g.quadraticCurveTo(cx + k * W * (j + 0.12), cy + H * 0.15, cx + k * W * (j + 0.18), bottom - (j === 0.3 ? step : -step) * 0.5);
    g.stroke();
  }
  g.lineWidth = lw; g.strokeStyle = INK;
  // Claws, raised.
  for (const k of [-1, 1]) {
    const clawX = cx + k * W * 0.55, clawY = cy - H * 0.32 + Math.sin(t * 3 + k) * s * 0.08;
    g.fillStyle = body;
    g.beginPath(); g.ellipse(clawX, clawY, s * 0.38, s * 0.28, k * 0.4, 0, Math.PI * 2); g.fill(); g.stroke();
    g.fillStyle = '#fff'; g.beginPath(); g.moveTo(clawX + k * s * 0.05, clawY - s * 0.05); g.lineTo(clawX + k * s * 0.38, clawY - s * 0.2); g.lineTo(clawX + k * s * 0.28, clawY + s * 0.02); g.closePath(); g.fill(); g.stroke();
  }
  // Shell.
  g.fillStyle = body;
  g.beginPath(); g.ellipse(cx, cy, W * 0.45, H * 0.36, 0, 0, Math.PI * 2); g.fill(); g.stroke();
  g.fillStyle = 'rgba(255,255,255,0.25)'; g.beginPath(); g.ellipse(cx - W * 0.12, cy - H * 0.14, W * 0.2, H * 0.08, -0.2, 0, Math.PI * 2); g.fill();
  // Eyes on stalks, looking where it's going; angry brows.
  for (const k of [-1, 1]) {
    const ex = cx + k * W * 0.24, ey = cy - H * 0.5;     // wide apart, so the crown sits between them
    g.beginPath(); g.moveTo(ex, cy - H * 0.3); g.lineTo(ex, ey); g.stroke();
    g.fillStyle = '#fff'; g.beginPath(); g.arc(ex, ey, s * 0.17, 0, Math.PI * 2); g.fill(); g.stroke();
    g.fillStyle = INK; g.beginPath(); g.arc(ex + dir * s * 0.06, ey + s * 0.02, s * 0.07, 0, Math.PI * 2); g.fill();
    if (angry) { g.beginPath(); g.moveTo(ex - s * 0.18, ey - s * (k === dir ? 0.12 : 0.24)); g.lineTo(ex + s * 0.18, ey - s * (k === dir ? 0.24 : 0.12)); g.lineWidth = lw * 1.4; g.stroke(); g.lineWidth = lw; }
  }
  // Mouth.
  g.beginPath(); g.arc(cx, cy + H * 0.02, s * 0.2, 0.15 * Math.PI, 0.85 * Math.PI); g.stroke();
  // Crown.
  const top = cy - H * 0.36, kx = cx;
  g.fillStyle = '#ffd23d';
  g.beginPath(); g.moveTo(kx - s * 0.3, top + s * 0.05); g.lineTo(kx - s * 0.33, top - s * 0.32); g.lineTo(kx - s * 0.15, top - s * 0.14); g.lineTo(kx, top - s * 0.42);
  g.lineTo(kx + s * 0.15, top - s * 0.14); g.lineTo(kx + s * 0.33, top - s * 0.32); g.lineTo(kx + s * 0.3, top + s * 0.05); g.closePath(); g.fill(); g.stroke();
  g.fillStyle = '#3db8ff'; g.beginPath(); g.arc(kx, top - s * 0.08, s * 0.07, 0, Math.PI * 2); g.fill();
  if (angry) {                            // steam
    g.fillStyle = 'rgba(255,255,255,0.7)';
    for (let k = 0; k < 3; k++) { const p = (t * 1.5 + k / 3) % 1; g.globalAlpha = 1 - p; g.beginPath(); g.arc(cx + (k - 1) * s * 0.4, top - s * 0.5 - p * s * 0.8, s * (0.08 + p * 0.12), 0, Math.PI * 2); g.fill(); }
    g.globalAlpha = 1;
  }
  g.restore();
}
